/**
 * World-wide tuning constants. Everything is in metres / seconds unless noted.
 * One tile is one metre. These are shared so the client can predict exactly
 * what the server simulates.
 */

/**
 * Radius of the playable cross-section of the Dyson sphere. The side view
 * looks at one slice through the sphere; its shell is the circular wall.
 */
/** The sphere's radius, in blocks. */
export const WORLD_RADIUS = 3_200;

/** Server simulation rate. */
export const TICK_RATE = 20;
export const TICK_MS = 1000 / TICK_RATE;
export const TICK_DT = 1 / TICK_RATE;

/**
 * Clients simulate and send input faster than the server ticks, so your own
 * movement is smooth and responsive. The server applies several inputs per
 * tick; each input carries its own dt so both sides stay in lockstep.
 */
export const INPUT_RATE = 60;
export const INPUT_DT = 1 / INPUT_RATE;

/** Other players are drawn this far in the past, so there are always two snapshots to blend between. */
export const INTERP_DELAY_MS = 100;

/** Every ship lives in a fixed-size build grid; the starter design sits in the middle. */
export const SHIP_GRID = { w: 80, h: 48 } as const;

export const SHIP = {
  /** Push per steam engine firing. Acceleration = engines firing that way * enginePower / (baseMass + tiles). */
  enginePower: 670,
  /** Small, so a lone block is light enough for ships to shove aside. */
  baseMass: 2,
  maxSpeed: 80,
  /** Velocity damping per second (the sphere is full of air). */
  drag: 0.45,
  /** Turning (Q/E): angular acceleration = steam engines * turnPower / (baseMass + tiles), rad/s^2. */
  turnPower: 30,
  /** Turning burns this much fuel and water per steam engine per second. */
  turnBurn: 0.2,
  /** Fastest a ship can spin, rad/s. */
  maxTurnRate: 1.6,
  angularDrag: 2.5,
  /** Each ship tile collides as a circle this size; slightly over half a tile so corners rarely clip. */
  cellRadius: 0.55,
  /** Extra damping per second when fully submerged in water (scaled by how much of the ship is under). */
  waterDrag: 1,
} as const;

/**
 * Steam. A ship's furnaces hold its fuel and water, both measured in
 * engine-seconds: one steam engine firing for a second uses 1 of each. Steam
 * engines only push while the ship has both. Feed a furnace standing by it:
 * coal or starbark (ITEMS[...].fuel), and water (ITEMS.water.water).
 */
export const FURNACE = {
  /** Each furnace adds this much to the ship's fuel and to its water. */
  capacity: 1200,
  /** How full a fresh starter ship's furnaces start, of fuel and of water. */
  starterFill: 0.8,
} as const;

/**
 * Ships without a helm (chunks broken off, blocks built in open space) don't
 * hold station: they drift on with very little drag.
 */
export const DRIFT = {
  drag: 0.04,
  angularDrag: 0.15,
  /** How fast a piece drifts clear of what it broke off from, m/s. */
  breakKick: 2.4,
  /** A block built in open space drifts off this fast, m/s. */
  placedDrift: 1.2,
  /** A cannonball knocking a block loose (or hitting a lone block) gives it this share of its velocity. */
  hitShove: 0.3,
  /** Past this many small, helmless, empty ships, the oldest disappears. */
  max: 200,
} as const;

/** What hurts, and how much. Health runs 0..max; at 0 you're knocked out. */
export const HEALTH = {
  max: 100,
  /** A cannonball hit. */
  cannonball: 35,
  /** Per second, this close to a sun's surface (tiles), getting worse closer in. */
  sunHeat: 20,
  sunHeatRange: 60,
  /** A hit with a sword of each tier, any other tool, or bare hands. */
  sword: { wood: 10, stone: 15, iron: 22 },
  toolHit: 6,
  punch: 3,
  /** How fast someone hit is knocked back, m/s. */
  knockback: 16,
  /** Seconds after the last hurt before health comes back, and how fast (per second). */
  regenDelay: 5,
  regen: 4,
  /** Eating spores (from glow fungi) gives this much back. */
  spores: 25,
  /** Being hit by a ship flying past faster than this (m/s) hurts... */
  shipBump: 10,
  /** ...this much per m/s over it. */
  shipBumpDamage: 0.8,
} as const;

/**
 * The steampack, worn on the back (an item: see GearSlot, wearsSteampack;
 * nobody starts with one). Off the rock, pushing any way flies you that way,
 * free and for as long as you like. Speed (blocks/s) tops out at `maxSpeed`
 * while it's on, though it never slows you below what you were already doing.
 */
export const STEAMPACK = { thrust: 90, maxSpeed: 18 } as const;

export const CHARACTER = {
  width: 1.4,
  height: 3.4,
  /** On asteroid ground the body's bottom corners are bevelled this much each way, so feet settle onto slopes (see tileCorners). */
  footBevel: 0.5,
  walkSpeed: 12,
  /** How fast your speed along the ground reaches what you're pushing for, per second. */
  groundAccel: 80,
  climbSpeed: 9,
  /**
   * Off the rock, with nothing pressed, you drift, the
   * sphere's air slowing you this much per second. Thin: how far a push
   * carries you is its speed over this, so a full brace (LEAP.maxSpeed)
   * coasts about 870 blocks and even a thrown fibre (THROW.light) is worth
   * fifteen. The steampack (STEAMPACK) flies you about for free out there;
   * the grapple (GRAPPLE) hauls you in.
   */
  airDrag: 0.03,
  /** How fast (rad/s) you turn upright on a ladder or aboard a ship. */
  turnRate: 8,
  /** How fast (rad/s) Q/E spin you round in open space (where there's no up to stand on). */
  spinRate: 3,
  /** Swimming in water: free movement in every direction, but slow and heavily damped. */
  swimAccel: 40,
  swimMaxSpeed: 10,
  swimDrag: 3,
  /** How far away you can build, measured from the middle of the body. */
  reach: 14,
  /** How near (from the middle of the body) a hatch, door, chest, bed, winch, sign or furnace must be to use it (right click). */
  useReach: 3.5,
  /** You can only dig blocks within this many tiles of your body. */
  digReach: 4,
} as const;

/** Cannonballs, as fired by turrets. */
export const CANNON = {
  speed: 140,
  /** Seconds. range = speed * life. */
  life: 3,
  /** Cannonballs drift very slightly toward the nearest sun. Purely for feel. */
  gravity: 6,
} as const;

export const WORLD_GEN = {
  /**
   * Each generation cell (see AREA.cellTarget) well inside the shell is a
   * star (a solar system, the sun at its middle) unless it's part of a dark
   * reach (see `reaches`): this share of the stars are cold blue ones of
   * another make, the rest warm. No star is home: new players start on the
   * stream (see STREAM).
   */
  systems: { cold: 0.15 },
  /**
   * Dark reaches (see stream_plan.md): cells with no star, grouped into
   * regions by a noise field over the sphere (`scale` cells across a blob):
   * the `share` of the cells where the noise is lowest are dark. They hold a
   * few things only (`things` for every 1600 by 1600, as WORLD_GEN.cells),
   * and `derelicts` times the usual wrecks.
   */
  reaches: { scale: 2.0, share: 0.3, things: 0.7, derelicts: 3 },
  /**
   * A star's ring (see generate.ts, systemLayout and belts): one ring of
   * rocks `width` wide, from just outside a flare's reach, going round at
   * `speed` blocks/s at its rim (the stream's way, if the stream goes round
   * the star; see STREAM). It holds `density` small rocks per square block
   * (a bigger one counts for as many as it's in the way of; rings differ by
   * `ringDensity`), at least `gap` apart: `small`, by `shares` `medium` and
   * `big` (see isBigRock), `water` water balls of `waterRadius`,
   * always a large rock of `large.radius`, and with `giant.chance` a giant
   * (as big as fits, up to `giant.radius`; the ring is at least `giant.width`
   * wide then).
   */
  ring: {
    width: [360, 560] as const,
    speed: 4,
    density: 3.5e-5,
    gap: 10,
    small: [7, 22] as const,
    medium: [24, 40] as const,
    big: [46, 90] as const,
    shares: { medium: 0.2, big: 0.04 },
    water: [0, 1] as const,
    waterRadius: [40, 64] as const,
    giant: { chance: 0.5, width: 600, radius: 230 },
    /** This share of the ring's medium rocks (plain, metal or ice) are vined (see WORLD_GEN.vined). */
    vined: { share: 0.5 },
    /** Rings aren't all alike: each holds `density` times this many (from sparse to packed). */
    ringDensity: [0.8, 2.0] as const,
    large: { radius: [70, 90] as const },
    /**
     * Systems differ in size (see systemRings): each star's rings reach out to
     * `reach` from it, by size, with `share` of the stars each size. A small
     * one is one ring (`width` wide or so); bigger ones are ring after ring,
     * each `ringWidth` wide with `lane` between, like the old home system. A
     * large one is never beside another, and neighbours give way so the
     * stream always fits between them. A sphere with one star keeps room for
     * the wild between the stream round it and the shell, so its star's system
     * is no bigger than that leaves.
     */
    sizes: {
      small: { share: 0.45, reach: [630, 900] as const },
      medium: { share: 0.35, reach: [900, 1450] as const },
      large: { share: 0.2, reach: [1450, 2050] as const },
    },
    ringWidth: [160, 420] as const,
    lane: 50,
  },
  /** Small rocks: the share of each kind by mineral zone (see minerals, mineralZone); the rest plain rock. See AsteroidDef.kind. */
  rockKinds: {
    hot: { metal: 0.3, hollow: 0.12, dead: 0.1 },
    mild: { ice: 0.1, hollow: 0.14, metal: 0.14, dead: 0.1 },
    cold: { ice: 0.4, hollow: 0.2, metal: 0.06, dead: 0.08 },
  },
  /**
   * What's in the rocks, by where they are. A rock's zone is by its heat (see
   * mineralZone): `hot` and over, the inner rings, rich in iron ore (and
   * ember ore where it's scorched); under `cold`, the outer rings and cold
   * suns' systems, ice and crystal; between, `mild`, coal by the wood.
   * `ore` and `coal`: how much of each there is against the usual;
   * `geodes`: the chance a rock big enough has a crystal-lined heart.
   * Rocks further from the stream (see STREAM) are richer: ore and coal up
   * by `wealth.per` a cell away from it, to `wealth.max`. And now and then a small
   * rock (up to `nuggets.radius`) is a nugget, almost all coal or iron ore,
   * with the chance of each by zone.
   */
  minerals: {
    hot: 0.55,
    cold: 0.25,
    zones: {
      hot: { ore: 1.6, coal: 0.6, geodes: 0.04 },
      mild: { ore: 1, coal: 1.4, geodes: 0.08 },
      cold: { ore: 0.7, coal: 0.8, geodes: 0.3 },
    },
    wealth: { per: 0.25, max: 0.6 },
    nuggets: { radius: 12, hot: { iron: 0.04, coal: 0.005 }, mild: { iron: 0.01, coal: 0.03 }, cold: { iron: 0.005, coal: 0.015 } },
  },
  /** Derelict ships adrift, with something in their holds: this many for the space the original 3200-radius world had. */
  derelicts: 2,
  sunRadius: [90, 150] as const,
  /**
   * What a cell with no sun (out by the shell) holds: `things` for every
   * 1600 by 1600 of it, each by share a field of small rocks, a big rock on
   * its own, or (the rest) open space.
   */
  cells: { things: 2, field: 0.4, lone: 0.16 },
  /**
   * A field: `rocks` small rocks of `rockRadius`, at least `gap` apart, in a
   * disc of `radius`. With `eddyChance` it's an eddy, turning round its middle
   * at `rimSpeed` (blocks/s) at its edge; either way its rocks drift (see
   * ROCK_MOTION).
   */
  field: { radius: [110, 230] as const, rocks: [7, 16] as const, rockRadius: [7, 22] as const, gap: 8, eddyChance: 0.5, rimSpeed: [3, 7] as const },
  /** A big rock on its own, in the middle of its open space. */
  lone: { radius: [46, 76] as const },
  /** Empty tiles around each asteroid's rock so players can build outward. */
  asteroidPad: 20,
  /** Rocks this big (radius) and bigger are round, turn their pieces to face out, and can have grazers (see isBigRock): the big ones of a sun's belts and its lone rocks. Never a water ball. */
  bigRadius: 46,
  /** Extra padding around the home asteroid so its tallest trees fit in its grid. */
  homeExtraPad: 32,
  /**
   * Life spreads to every rock that gets the light for it (AsteroidDef.lush),
   * by the gap from a sun's surface to the rock's: bare where a flare's heat
   * reaches (`scorched`, HEALTH.sunHeatRange times FLARES.reach), lushest in
   * the `warm` band, thinning to bare rock by `dark`: far out, so most rocks
   * between the stars get some. A cold sun gives `coldLight` of the light.
   * Grass needs `grass` of it, trees `trees`. Heat falls off sooner, to
   * nothing by `heatReach` (see heatAt).
   */
  life: { scorched: 210, warm: [450, 2400] as const, dark: 4000, heatReach: 1900, coldLight: 0.6, grass: 0.1, trees: 0.3 },
  /**
   * Heat and wet (see environment.ts): a cold sun gives `coldHeat` of the
   * heat; wetness fades over `waterReach` from the water ball, is
   * `dampWet` in the `damp` share of rocks (wherever they are), up to
   * `iceWet` in an ice rock, give or take `jitter`.
   */
  env: { coldHeat: 0.15, waterReach: 1200, damp: 0.18, dampWet: [0.6, 0.9] as const, iceWet: 0.9, jitter: 0.3 },
  /** Biome thresholds (see biomeOf): heat for scorched, below `cold` is cold; light for meadow, twilight, lichen; wet for grove and frost. */
  biomes: { scorched: 0.98, cold: 0.25, meadow: 0.6, twilight: 0.3, lichen: 0.1, groveWet: 0.5, frostWet: 0.55 },
  /**
   * Living rocks (see lifeasteroids.md, "Living rocks") grow at `heat` and
   * more (just outside a flare's reach): a lone rock there is a living one
   * with `chance`. Their radius grows from `radius[0]` to `radius[1]` the
   * closer they are; branches reach `reach` of the radius past the rock.
   * Those of `heartRadius` and more have a heart.
   */
  living: {
    /** Switched off for now: where a living rock would be is an ordinary rock. */
    enabled: false as boolean,
    heat: 0.78,
    chance: 0.5,
    radius: [22, 52] as const,
    reach: 0.7,
    bark: [4, 8] as const,
    heartRadius: 40,
  },
  /** Shape families of small rocks (see rocks.ts), by share; the rest are round. */
  shapes: { long: 0.2, twin: 0.14, cracked: 0.12, shattered: 0.08 },
  /**
   * A vined rock (see AsteroidDef.vined): one rock in `lobes` lumps round its
   * middle, at least `gap` blocks of open space between neighbours (however
   * their outlines lie), tied together by vines. Zero-g, so the vines are
   * the way from one lobe to the next.
   */
  vined: { lobes: [3, 4] as const, gap: 6 },
} as const;

/**
 * Plants (see lifeasteroids.md, "Flora"): felled or harvested whole with
 * any part of one, `chop` times as long as one of its tiles, dropping a few
 * of each thing in `drops`; they grow back after `regrowMinutes`. An area
 * looks for plants ready to grow back every `checkEvery` s.
 */
export const PLANTS = {
  checkEvery: 10,
  kinds: {
    tree: { chop: 6, regrowMinutes: 25, drops: { starbark: [6, 10], fiber: [2, 4], sapling: [0, 1] } },
    reeds: { chop: 2, regrowMinutes: 10, drops: { fiber: [3, 5] } },
    shrub: { chop: 2, regrowMinutes: 15, drops: { fiber: [1, 3], lichen: [0, 1] } },
    glowFungus: { chop: 2, regrowMinutes: 30, drops: { glowcap: [1, 3], spores: [1, 2] } },
    frostFlower: { chop: 1, regrowMinutes: 45, drops: { frostFlower: [1, 1] } },
    roots: { chop: 3, regrowMinutes: 30, drops: { fiber: [2, 4] } },
    vine: { chop: 2, regrowMinutes: 20, drops: { rope: [1, 2], fiber: [1, 3] } },
    thistle: { chop: 2, regrowMinutes: 15, drops: { fiber: [2, 3] } },
    fern: { chop: 2, regrowMinutes: 15, drops: { fiber: [2, 4] } },
    frostGrass: { chop: 1, regrowMinutes: 20, drops: { ice: [1, 2] } },
    cinderBloom: { chop: 2, regrowMinutes: 40, drops: { emberOre: [0, 1], coal: [1, 2] } },
    lichenFingers: { chop: 2, regrowMinutes: 25, drops: { lichen: [1, 2] } },
    glowDrops: { chop: 2, regrowMinutes: 30, drops: { glowcap: [1, 2] } },
  },
  /** Glow bulbs a felled lantern or dusk tree drops besides (its glowing fruit). */
  fruit: [1, 2] as const,
} as const;

/**
 * Living bark heals (see lifeasteroids.md, "Living rocks are alive"): a dug
 * bark or sapwood tile grows back `minutes` after it was dug, once a side of
 * it touches living wood, at most `perCheck` tiles per rock each check
 * (every `checkEvery` s). Not within `braceReach` of a brace, nor within
 * `clearance` of anyone or anything.
 */
export const HEALING = { minutes: 4, checkEvery: 15, perCheck: 3, braceReach: 4, clearance: 2 } as const;

/** Vines between living rocks (see VineDef): cut after `chop` s of swinging an axe or sword, grow back after `regrowMinutes`. Climbed at `climbSpeed` (blocks/s). Let go of with G, one doesn't take hold again for `regrab` s. Pushing off one takes `pushOff` of a full push, less than `pushAlong` of it along the vine: you leave at `kick` blocks/s, and it can't take hold again for `regrab` s. */
export const VINES = { chop: 3, regrowMinutes: 20, climbSpeed: 7, grabReach: 2.5, reach: 3, regrab: 0.5, pushOff: 0.5, pushAlong: 0.5, kick: 8 } as const;

/**
 * Rotating rings (see rings.ts): the rings turn slowly, the inner ones
 * faster, so neighbours slide past each other a little.
 */
export const RINGS = {
  /** Ring 1 turns once in this long (s); each ring further out `periodGrowth` times longer again. */
  innerPeriod: 6 * 3600,
  periodGrowth: 0.12,
  /** How far past an edge something goes before it changes area (as AREA.hysteresis). */
  hysteresis: 24,
} as const;

/**
 * New rocks (see lifeasteroids.md, "New rocks"): each cell has `perDay`
 * chances a day of one forming, only out of sight (`unseen` from every
 * player), and only while it has fewer rocks than it was made with. Never in
 * a sun's belts: they form in the open between.
 */
export const NEW_ROCKS = { perDay: 1.6, checkEvery: 60, unseen: 900, radius: [20, 44] as const } as const;

/**
 * Bump when world generation changes: the Node server's saves are kept per
 * version, so a world made differently starts fresh.
 */
export const WORLD_GEN_VERSION = 46;

export const NET = {
  /**
   * Each player is only sent the players, ships and cannonballs this close (in
   * tiles; ships count from their edge). Ships already sent stay until a bit
   * further out, so one at the edge doesn't flicker in and out.
   */
  viewRadius: 800,
  forgetRadius: 960,
} as const;

/**
 * Only what's near someone is real (see plan.md, "Waking"): rocks within
 * `rocks` of a player (from their middle, plus their own size) are built,
 * tiles, collider and all, and let go again past `rocksDrop`; ships within
 * `ships` are simulated in full, and past `shipsSleep` only coast on (no
 * collisions) till someone comes. Checked every `every` s. Ships wake well
 * inside the rocks that are built, so a ship never meets a rock that isn't.
 *
 * Building a rock takes a few ms, so they're built nearest first, at most
 * `budget` tiles' worth of them (their grids' size) a server tick, and
 * `clientBudget` ms of it a frame on the client, till all are; but someone
 * put down somewhere (joining, teleporting) has everything within `near` of
 * them built at once. (The server counts tiles, not time: on Cloudflare the
 * clock stands still while code runs, so a time budget never runs out.) An area never has more
 * than `maxBuilt` built (the nearest kept), whoever's about: they're most of
 * its memory, and a Durable Object has 128 MB.
 */
export const WAKE = { rocks: 1050, rocksDrop: 1350, ships: 850, shipsSleep: 1100, every: 0.5, near: 400, budget: 60_000, clientBudget: 6, maxBuilt: 300 } as const;

/**
 * Ships take damage. Cannonballs (and ramming) break blocks, tough ones over
 * several hits (TileDef.hits); timber catches fire.
 */
export const DAMAGE = {
  /** Ramming: a change of speed in one physics step (blocks/s) above this hurts the ship and crew. */
  impactSpeed: 20,
  /** Every this much more speed lost is another hit (on the next block back, and the crew). */
  impactPerHit: 10,
  maxImpactHits: 6,
  /** Health each impact hit costs everyone aboard. */
  crewPerHit: 8,
} as const;

/** Fires on timber: started by cannonballs, spreading, burning blocks away, put out with water. */
export const FIRE = {
  /** Chance a cannonball hit sets the timber beside it alight. */
  igniteChance: 0.4,
  /** Seconds before a burning block is gone. */
  burnTime: 12,
  /** Every this many seconds a fire may spread to a block beside it... */
  spreadEvery: 3,
  /** ...with this chance. */
  spreadChance: 0.5,
  /** Health per second lost standing in fire. */
  hurt: 10,
} as const;


/**
 * Pirates: now and then a pirate ship comes out of the dark after someone
 * away from the home asteroid, keeps its distance and shoots. Cripple it
 * (shoot off its helm or turret, or a good part of it) and its chest of
 * plunder is yours for the taking.
 */
export const PIRATES = {
  /** Seconds between one showing up and the next, at random in this range. */
  every: [60, 150] as const,
  /** At most this many at once (and never more than there are players). */
  max: 3,
  /** Not within this far of the stream (see STREAM, streamDistance), where everyone starts: it's tame there. */
  safeDistance: 1200,
  /** They appear this far from whoever they're after. */
  spawnDistance: 300,
  /** They lose interest in anyone further than this, and leave once nobody's within NET.forgetRadius. */
  chaseRange: 600,
  /** How far off they like to hang while they shoot. */
  standoff: 80,
  /** And they open fire within this range. */
  fireRange: 240,
  /** How wide of the mark they shoot (radians, at random either way). */
  aimSpread: 0.06,
  /** Crippled once it's down to this share of its blocks (or lost its helm or turret). */
  crippledAt: 0.7,
  /** What's in a pirate's chest: [item, least, most]. */
  plunder: [
    ["iron", 4, 10],
    ["coal", 6, 14],
    ["water", 4, 10],
    ["crystal", 0, 3],
  ] as const,
} as const;
/** Pirate ships come in kinds; the further from the stream their prey is, the tougher the kind. */
export const PIRATE_KINDS = {
  /** [kind, chance] by how far (tiles) the prey is past the stream's tame zone (see PIRATES.safeDistance): nearest band first. */
  bands: [
    [2000, [["scout", 0.7], ["pirate", 0.3]]],
    [5000, [["scout", 0.2], ["pirate", 0.6], ["brute", 0.2]]],
    [Infinity, [["pirate", 0.3], ["brute", 0.4], ["captain", 0.3]]],
  ] as const,
  /** A scout turns tail once it's down to this share of its blocks. */
  scoutFlees: 0.92,
  /** A captain's chests hold this many times the usual plunder, and more crystal. */
  captainPlunder: 2,
} as const;

/** Pirate bases: an asteroid held by pirates, its turrets firing at anyone who comes near. See AsteroidDef.pirateBase. */
export const PIRATE_BASE = {
  /** Not within this far of the stream (see streamDistance): only on rocks well off the line. */
  minDistance: 3000,
  turrets: 3,
  /** Its turrets open fire within this range. */
  fireRange: 260,
  aimSpread: 0.08,
  plunder: [
    ["iron", 12, 24],
    ["coal", 10, 20],
    ["crystal", 3, 8],
    ["glass", 4, 10],
  ] as const,
} as const;

/** Ships grow: building within `margin` of a grid's edge pads it by `pad` on every side, up to `max` across. */
export const SHIP_GROWTH = { margin: 2, pad: 16, maxW: 240, maxH: 176 } as const;

/**
 * What you can carry: this many slots, each a stack of up to `stack` of one
 * thing. The first `hotbar` of them are the hotbar (keys 1-9), the rest the
 * main grid (see slots.ts): 9 + 27, as in Minecraft.
 */
export const INVENTORY = { slots: 36, stack: 99, hotbar: 9 } as const;

/** A chest's slots (Minecraft's 27), and a cargo crate's one slot: one kind of thing, up to `holds` of it. */
export const CONTAINER = { chest: 27, crateHolds: 300 } as const;

/** Undo (Ctrl+Z): a block you just placed comes back to you, if you're quick. */
export const UNDO = { seconds: 6, max: 20 } as const;

/** Rotation assist at the helm: the ship stops spinning when you let go of Q/E, burning a little steam to do it. */
export const ASSIST = {
  /** How hard it works against the spin, as a share of full turning. */
  strength: 1,
  /** Below this spin (rad/s) it rests. */
  deadband: 0.02,
} as const;

/** Pumps: water (engine-seconds) per second each adds to the furnaces while under water. */
export const PUMP = { rate: 12 } as const;

/** Winches reel in loose blocks (helmless ships of up to `maxMass` tiles) within `reach`, holding them `hold` from the winch. */
export const WINCH = { reach: 14, hold: 4, maxMass: 6, spring: 6 } as const;

/** Bow guns: the pilot fires them all with a click, straight along the ship. */
export const BOW_GUN = { cooldown: 1.6 } as const;

/** A patch kit mends one cracked block (or piece) on a ship. */
export const REPAIR = { cooldownMs: 1200 } as const;

/**
 * The grapple: holding it, right click throws a line toward the mouse; it
 * flies out at `throwSpeed` and hooks the first rock or ship it reaches within
 * `range` (28 blocks, about 50 feet), or comes back empty. Hooked, the line
 * holds you at its length (pulled back in at `stiffness` blocks/s per block
 * past it); hold W to reel
 * in, pulled at `pull` up to `maxSpeed`, down to `letGo` from the hook. Reeled
 * in that close onto something you stand on or hold, it comes off; right click,
 * or a leap off the ground, lets go.
 */
export const GRAPPLE = { range: 28, throwSpeed: 60, pull: 70, maxSpeed: 26, letGo: 2.5, stiffness: 10 } as const;

/**
 * There's no gravity anywhere, and no magic feet: everything is holding on
 * with your hands. Touch rock (within `reach` of a side of you) and you hold
 * it, drawn in (`settle`, blocks/s per s) and held to it (`stick`, blocks/s)
 * however you are turned — nothing swings your body round (only Q/E turn it).
 * Pushing along the rock crawls you that way, by the screen: `crawlBare` on
 * bare rock, `crawlSpeed` with something growing within `plantReach` of your
 * middle to pull on (see isGrowth). You crawl up its walls and round its
 * underside, kept to it over a crest within `snap` blocks. The way along is
 * felt from the rock itself (see surfaceUp), used as a direction only.
 *
 * No push ever lets go of what you hold, whichever way it points: bracing
 * (see LEAP) is the only way off, so a thumb on the stick or a key held a
 * moment too long can't throw you into open space by accident. A push away
 * from every side you have hold of at once (within `away` of straight away,
 * so a diagonal still crawls) simply moves you nowhere.
 *
 * Aboard a ship you hold onto its floor, walls and ceiling, stopping (`hold`,
 * per s) when you push nothing, crawling along them at `crawlSpeed`, and
 * bracing off them as off rock. Floating free aboard, the air slows you
 * (`drag`, per s) and pushing paddles you through it, gaining `paddle`
 * blocks/s each s up to `paddleSpeed`. Out in the world there's nothing to
 * push on: you go where your last push sent you, slowed by CHARACTER.airDrag,
 * unless you throw something (see THROW).
 */
export const GRIP = { reach: 0.4, hold: 12, crawlSpeed: 10, crawlBare: 4, plantReach: 3.5, stick: 3, settle: 40, snap: 2, away: 0.85, drag: 0.6, paddle: 3, paddleSpeed: 2 } as const;

/**
 * Bracing off rock, or off a hull aboard a ship: hold Space (touch: Leap) to
 * brace against it, `charge` s to full strength, and let go to push off toward
 * the aim, at from `minSpeed` (a tap: about 270 blocks of coast) to `maxSpeed`
 * (about 870). Always away from what you hold: at least `minOut` of the way
 * out from it. Your hands won't take hold again for `wait` s.
 *
 * This is the only way off what you hold (see GRIP): nothing you push with
 * lets go, so you leave a rock or a hull when you mean to and not before.
 */
export const LEAP = { charge: 0.6, minSpeed: 8, maxSpeed: 26, minOut: 0.2, wait: 0.35 } as const;

/**
 * Throwing (see movePlayer, weightOf): G throws one of whatever is in your
 * hand toward the aim and you go the other way, at `light`, `medium` or
 * `heavy` blocks/s by what it weighs — adrift with no steampack, throwing
 * your pickaxe away from where you want to go is how you get there. Holding
 * on, the rock takes the push and you don't move — so the same key, with a
 * hand on the rock or a hull, is how you put something down: there being no
 * floor to set it on, it's simply let go of, and hangs at arm's reach going
 * nowhere (and goes round with the rock, as you do).
 *
 * Either way the item is left in the world as a thing (see DROPS), turning
 * end over end `spin` radians a second, for whoever reaches it to pick up.
 */
export const THROW = { light: 0.5, medium: 1.5, heavy: 3, speed: 22, spin: 7 } as const;

/**
 * Loose items (see drops.ts). Anything thrown or dropped is a pile of one
 * kind of thing, drifting where it was let go of: nothing falls in here, so
 * it hangs in the air, turning slowly, until it fetches up against rock or a
 * hull. Come within `reach` of one (in the same frame: aboard the same ship,
 * or both out in the world) and it goes into your pack, as many as there's
 * room for; for `wait` seconds after it's dropped nobody can, and whoever
 * put it down can't take it back while they're still standing in it, so
 * something set down beside you stays set down until you move off it.
 * Piles of the same thing within `merge` of each other run together, up to
 * INVENTORY.stack. A pile is gone `life` seconds after it was last touched,
 * and an area holds at most `most` of them, the oldest going first.
 */
export const DROPS = { reach: 2.4, wait: 1.2, merge: 1.6, life: 300, most: 240, spin: 1.8 } as const;

/**
 * Harpoons: manned like a turret, firing a line along the barrel out to
 * `range`. It anchors in rock, sticks in a whale or gas bag (hurting it by
 * `damage`; a whale tows the ship while it struggles, a gas bag's towed) or
 * hooks another ship to tow. The line pulls in with `stiffness` once it's
 * stretched; holding fire reels it in at `reel` blocks/s, down to
 * `minLength`. `cooldown` seconds between shots.
 */
export const HARPOON = { range: 90, stiffness: 3, reel: 8, minLength: 5, damage: 15, cooldown: 1 } as const;

/** Lanterns light up what's within `radius` of them; `strength` is the extra brightness right beside one. */
export const LIGHT = { radius: 9, strength: 1.4 } as const;

/** Currents (the eddies turning rock fields round; see CurrentDef.spin), which carry ships and drifters along. */
export const CURRENTS = {
  /** How quickly something is brought up to the current's speed, per second (ships; people drifting get twice this). */
  grip: 0.18,
} as const;

/**
 * Drifting rocks (see rockMotion.ts): rammed by a ship or bumped by another
 * rock, a field rock is knocked off its path, and swings back to it.
 */
export const ROCK_MOTION = {
  /** The pull back to its path (1/s^2) and the drag on the knock (1/s): it settles in half a minute or so. */
  spring: 0.02,
  damping: 0.3,
  /** Never knocked further than this from its path (so it stays in its own cell), nor faster than `maxSpeed` (blocks/s). */
  maxKnock: 32,
  maxSpeed: 12,
  /** A rock weighs this much per tile of its disc (a ship weighs 1 per block). */
  density: 0.3,
  /** Rocks bump as discs `size` times their radius, keeping `bounce` of the speed they met at. */
  size: 1.1,
  bounce: 0.45,
  /** A knock this small (offset and speed) is over: the rock is back on its path. */
  rest: 0.05,
} as const;

/** Space whales: slow, drifting, neutral until hurt; then they charge whoever hurt them. Their oil burns long. */
export const WHALES = {
  max: 4,
  health: 160,
  speed: 5,
  chargeSpeed: 22,
  /** How long they stay angry after the last hurt (s). */
  angerTime: 20,
  /** Hurt done ramming someone. */
  ram: 25,
  /** Oil a whale gives up, to whoever finished it. */
  oil: [4, 8] as const,
  /** Radius of the body, for hits. */
  radius: 6,
  /** Never closer than this to the stream (see streamDistance), where everyone starts. */
  awayFromStream: 1500,
} as const;

/**
 * Gas bags: big slow balloons of creatures that drift from tree to tree among
 * the rocks round their home, eating the leaves (`eat` seconds a tree).
 * Harmless: hurt one and it flees at `flee`; kill it and it leaves fibre (and
 * now and then a glow bulb). Where they live: see WILDLIFE and bands.ts.
 */
export const GASBAGS = {
  health: 30,
  speed: 3,
  flee: 9,
  /** Radius of the bag, for hits. */
  radius: 3,
  eat: [10, 20] as const,
  /** How far it looks for the next tree. */
  sees: 260,
  fiber: [2, 4] as const,
  bulb: 0.3,
} as const;

/**
 * Grazers: small shy beasts in herds on rocks that pull, with grass or moss,
 * walking the surface and stopping to graze. Hurt one and it bolts at `run`;
 * kill it and it leaves food (spores). Where they live: see WILDLIFE and
 * bands.ts.
 */
export const GRAZERS = {
  health: 20,
  speed: 2.2,
  run: 7,
  /** Radius of the body, for hits (its middle is this far up from its feet): a longneck, tall on its legs. */
  radius: 1.6,
  graze: [3, 9] as const,
  walk: [2, 6] as const,
  spores: [1, 2] as const,
  /** The highest ledge (blocks) it'll step up or down. */
  step: 1.6,
} as const;

/**
 * Skyfish: schools of `fish` circling the green rocks (and swimming in the
 * water balls), `out` past the rock's edge, at `speed` blocks/s; strung
 * `spacing` apart. Come within `scatter` of one and the school scatters,
 * fleeing `flee` times as fast for `fright` seconds. One hit kills a fish
 * (`radius` to hit), and it's food (a skyfish). Where they live: see
 * WILDLIFE and bands.ts.
 */
export const SKYFISH = {
  fish: [4, 8] as const,
  out: [3, 9] as const,
  speed: 4,
  spacing: 3.2,
  radius: 1.3,
  scatter: 7,
  flee: 2.5,
  fright: 3,
} as const;

/**
 * Resident wildlife (see bands.ts and wildlife_plan.md): grazers, gas bags
 * and skyfish live in bands with a home rock, made from the rock's seed and
 * remembered by its area. A band awake and calm plays a script the server
 * wrote ahead (`script` s long, keys every `keyEvery` s while moving); one
 * that's disturbed is run live until it's been calm for `calm` s.
 */
export const WILDLIFE = {
  /** A script runs this long (s), at random in this range; the next is written `lead` s before it ends. */
  script: [30, 90] as const,
  lead: 3,
  /** The writer's step (s), by kind: grazers turn back at ledges, gas bags drift slowly, schools only go round. */
  step: { grazer: 0.25, gasbag: 0.5, skyfish: 1 },
  /** While moving, a key at least this often (s). A school is always moving, and turns well under half a circle between keys. */
  keyEvery: { grazer: 0.5, gasbag: 1, skyfish: 2 },
  /** Scripts are written for at most this long a tick (ms; always one at least): the rest wait (a band just woken isn't out till it has one). */
  writeMs: 3,
  /** How often calm bands are checked for someone near (s). */
  check: 0.5,
  /** Skittish kinds look up at someone within `notice` (blocks), and bolt within `bolt`. Gas bags and whales don't notice people. */
  notice: { grazer: 14, skyfish: SKYFISH.scatter },
  bolt: { grazer: 5 },
  /** A live band calm this long (s: nobody within notice, not scared, not on a line) goes back to a script. */
  calm: 5,
  /** A band short of members gets one back every `refill` ms (caught up on waking). */
  refill: 20 * 60_000,
  /** Gas bags keep to trees within `range` of their home rock's middle. */
  range: 320,
  /** Herd size, for grazers; bags in a flock. */
  herd: [2, 6] as const,
  flock: [2, 4] as const,
} as const;

/** Sun flares: every so often a sun's heat reaches much further. A glow warns of one coming. */
export const FLARES = {
  /**
   * Each sun's time is cut into slots this long (s); in each, it flares once
   * with chance `chance`, at a moment fixed by the seed. A pure function of seed
   * and time (see flareAt), so every area near a sun agrees with no coordinator.
   */
  slot: 600,
  chance: 0.8,
  warning: 12,
  length: 20,
  /** How many times further than usual the heat reaches during one. */
  reach: 3.5,
} as const;

/** Grass and plants on asteroids burn too. */
export const GROUND_FIRE = {
  /** Chance a cannonball (or a crash) sets grass or plants alight. */
  igniteChance: 0.5,
  burnTime: 8,
  spreadEvery: 1.5,
  spreadChance: 0.55,
} as const;

/** Being knocked out costs you: you drop your held tool and some of a few other stacks, in a crate where you fell. */
export const KNOCKOUT = { stacks: 3, share: 0.3 } as const;

/** Rates for messages that aren't inputs or edits: chat, crafting, chests and the like. */
export const RATE_LIMIT = {
  /** Chat messages: a burst of this many, then one every `chatEvery` seconds. */
  chatBurst: 4,
  chatEvery: 2,
  /** Everything else: this many a second. */
  messages: 40,
  /** Tile edits applied across all players in one tick. */
  editsPerTick: 120,
} as const;

/** Turrets: the gun swings across the top of the turret and fires where it points. */
export const TURRET = {
  /** The barrel's arc, in the ship's frame (0 = toward the bow, pi = toward the stern). */
  minAim: 0.05,
  maxAim: Math.PI - 0.05,
  /** Seconds between shots. */
  cooldown: 1.2,
  /** Length of the barrel, tiles: shots leave from its tip. */
  barrel: 3.2,
} as const;

/**
 * The sphere shuts down (see night_plan.md and stream_plan.md): now and then
 * every star goes out at once, cold ones too, and the whole sphere has a
 * night. When is a pure function of the seed and the wall-clock time (see
 * shutdownAt), like flares: time is cut into `slot`s, and in each the sphere
 * shuts down with `chance`, at a moment fixed by the seed, so every area,
 * segment and client agrees without being told. Everyone gets `warning` s of
 * notice first (a countdown, see shutdownWarning: long enough to get back
 * to the stream from a venture). Then the stars falter (`falter` s,
 * flickering: the last call), dim (`dimming` s, the dark walking in from
 * the edge of each star's reach to the star), are out (`out` s), and relight
 * (`relight` s, the light sweeping back out).
 *
 * Everything the suns light at `hush` or more is under the Hush: nothing
 * electrical works there, and nothing from outside the sphere can come in.
 * Where it lifts during a shutdown, rifts open near people (see INCOMERS);
 * a player's light (lanterns, a lit furnace, fire) holds a rift off, and hurts
 * anything that came through (`lightDamage` a second, slowed to `lightSlow`).
 * Light coming back stalls whatever's still here: it freezes for `stall` s,
 * taking `stallDamage` times the hurt, and is pulled back out.
 */
export const NIGHT = {
  hush: 0.08,
  slot: 4 * 3600,
  chance: 0.75,
  warning: 600,
  falter: 90,
  dimming: 240,
  out: 360,
  relight: 180,
  /** How soft the edge of the dark is as it walks in (and the light as it sweeps out), blocks. */
  edge: 160,
  /** A fire or a lit furnace lights this far round it (lanterns: LIGHT.radius times their reach). */
  fireLight: 7,
  lightDamage: 2,
  lightSlow: 0.8,
  stall: 20,
  stallDamage: 2,
  /**
   * Rifts: one opens every `every` seconds near someone in the dark (twice as
   * often while a warden stands), `near` blocks from them, taking `open` s to
   * open; none in the last `lastMinute` s before the relight. At most `max`
   * incomers in an area (and `perPlayer` for each player there).
   */
  rift: { every: [6, 14] as const, near: [12, 40] as const, open: 3, lastMinute: 60, max: 10, perPlayer: 3 },
} as const;

/**
 * The incomers: things from outside the sphere that come in through rifts
 * while the sphere is shut down (see NIGHT). What comes is by how far from
 * the stream (`bands`: [distance from it, see streamDistance, [kind,
 * chance]...]); on the stream itself, spiders only.
 *
 * - spider: a robot harvestman on legs out of all proportion. Walks the rock,
 *   steps over ledges up to `step`, and goes for anyone within `sees`,
 *   biting `bite` every `biteEvery` s within `reach`. Never flees.
 * - eel: a chain of iron cages with something pale alive in each, swimming
 *   the dark at `speed`. Circles a ship for `circle` s, then latches onto its
 *   hull: the arc between its jaws stalls the engines within `stallReach`
 *   and burns the block it holds (a hit every `burnEvery` s). A hit makes it
 *   let go and flee at `flee` for `fleeFor` s. Bites people in the open.
 * - carrier: an iron gantry under a rotor, `hover` blocks over a rock,
 *   lowering `drops` folded spiders onto it every `dropEvery` s. Rises away
 *   at `flee` when hurt, and comes back.
 * - warden: the built thing, once a shutdown at most, only well off the
 *   stream. Stands on a rock holding its rift open (rifts come twice as
 *   fast), walks toward people at `speed`, and every `throwEvery` s tears a
 *   block of rock out of the ground and throws it at whoever's within
 *   `range` (a cannonball's hurt).
 *
 * `radius` is for hits (round a point `height` up from the feet of those
 * that walk); drops are [least, most], or a chance.
 */
export const INCOMERS = {
  spider: { health: 40, speed: 3.2, wander: 1.2, sees: 30, reach: 3.4, bite: 10, biteEvery: 1.2, step: 3, radius: 1.6, height: 2.3, wire: [2, 3] as const, cell: 0.3 },
  eel: { health: 45, speed: 9, flee: 14, fleeFor: 10, sees: 90, circle: [6, 10] as const, ring: 14, stallReach: 4, burnEvery: 3, bite: 8, biteEvery: 1.5, radius: 1.2, length: 10, scrap: [2, 4] as const, wire: [1, 2] as const, cell: 0.3 },
  carrier: { health: 50, speed: 3, flee: 6, fleeFor: 8, hover: 9, dropEvery: 40, drops: 2, lower: 2, radius: 2.4, scrap: [3, 5] as const, lens: 0.4 },
  warden: { health: 300, speed: 1.5, throwEvery: 4, windUp: 1.2, range: 40, sees: 60, radius: 2.6, height: 3.6, scrap: [8, 12] as const, cells: [2, 3] as const, lens: [1, 2] as const },
  bands: [
    [600, [["spider", 1]]],
    [3000, [["spider", 0.55], ["eel", 0.45]]],
    [Infinity, [["spider", 0.45], ["eel", 0.35], ["carrier", 0.2]]],
  ] as const,
  /** Wardens only come this far from the stream, at most one a shutdown in an area. */
  wardenFrom: 2000,
} as const;

/**
 * The rock stream (see stream_plan.md): a long river of asteroids on a closed
 * track that loops round the sphere like a race track, sweeping round the
 * stars and sneaking through the reaches, which players land on, build on
 * and ride. In the stream's own frame the rocks don't move (a conveyor
 * belt), so where a rock is follows from the clock (see rockMotion.ts).
 *
 * - `speed`: the belt's, blocks/s, the same all the way round. Under the
 *   steampack's 18, so a rock can be caught on foot; three times a ring's
 *   rim, so a ring turning the stream's way is a step across.
 * - `band`: how wide the band of rocks is (every rock's outline inside it); at
 *   a `narrows` it pinches to `narrows.width` over `narrows.length`, the
 *   rocks drawn in with it. Everything static keeps its outline `corridor`
 *   or more from the track's centre line (the band and a gap: banks sit
 *   there, `bankGap` and more past it), a gorge's channel is just wider than
 *   the band, and a star's ring ends `ringGap` inside the band's edge where
 *   the stream goes round it.
 * - Pieces: a `sweep` is an arc round a star of at least `sweepTurn` of a
 *   turn (less is a `glance`); `bend` circles of `bend.radius` go in the
 *   open between stars, `bend.room` or more apart, displaced `bend.offset`
 *   times their radius off the line (a `hairpin` wraps `hairpinTurn` of a
 *   turn or more); straights longer than `narrows.minStraight` may get a
 *   narrows or a gorge (a cut through a static rock of `gorge.radius`).
 *   The track stays `shellGap` inside the shell.
 * - Rocks: big ones (`big`, which pull) toward the middle of the band,
 *   medium ones about it and small ones (`small`) out to its edges, by
 *   `shares`; grids padded `pad` (big rocks `bigPad`), scattered over the
 *   band till their grids cover `fill` of it, and never two so close their
 *   grids could overlap anywhere on the loop (the inner edge of the tightest
 *   bend, and a narrows, included); `named` big
 *   rocks of `namedRadius` ride the loop, spaced evenly, where new players
 *   start; and a large asteroid of `large.radius` about every `large.every`
 *   blocks, mid-band. `segment` is about how much belt one segment owns.
 * - The flow (see flowAt): among the band's rocks the air goes with them,
 *   and with a ring's round its star, fading out over `flowEdge` past the
 *   edge; whatever floats there steers and is slowed against it, not still
 *   space, and someone drifting (or a ship without a helm) is brought round
 *   to it at `grip` a second more.
 * - `settle`: the share, per second, of a knock's across-band part that
 *   sticks as the rock's rest offset (see stepKnocks), so bumped rocks find
 *   new places in the band but never leave it.
 * - `lit`: how much sunlight a named rock needs for a new player to be put
 *   down on it; `starAhead`: and a star within this many seconds ahead.
 * - `twin` (see twin_streams_plan.md): a sphere with stars enough has
 *   several streams, one for about every `perStream` stars (each at least
 *   `minStars`), each a loop through its own patch of them, and each beside
 *   a neighbouring one for `minShared` to `maxShared` blocks, flowing the
 *   other way, `gap` band edge to band edge: one steampack hop, and more
 *   than two flow edges, so each belt's air keeps to itself. Never nearer
 *   each other than that anywhere. Laid by trying up to `splits` ways of
 *   splitting the stars, `candidates` stretches of the first stream to run
 *   beside, bends onto and off it `lead` along a straight from its ends,
 *   and `tries` rolls for the first stream and for each beside it.
 */
export const STREAM = {
  speed: 12,
  band: 450,
  corridor: 305,
  bankGap: 10,
  ringGap: 140,
  shellGap: 320,
  sweepTurn: 0.4,
  hairpinTurn: 0.38,
  bend: { radius: [800, 1100] as const, room: 3400, offset: [1.05, 1.6] as const, chance: 0.75 },
  narrows: { width: 320, length: 700, minStraight: 2000, chance: 0.4, wall: [18, 30] as const },
  gorge: { radius: [340, 380] as const, minStraight: 2400, chance: 0.3 },
  kerbs: { every: 260, radius: [16, 34] as const },
  lone: { every: 700, radius: [24, 44] as const },
  small: [7, 20] as const,
  medium: [24, 38] as const,
  big: [48, 72] as const,
  shares: { medium: 0.22, big: 0.05 },
  fill: 0.4,
  pad: 4,
  bigPad: 20,
  named: 4,
  namedRadius: [78, 92] as const,
  large: { every: 5000, radius: [150, 178] as const },
  grip: 0.8,
  flowEdge: 60,
  segment: 1100,
  settle: 0.12,
  lit: 0.35,
  starAhead: 900,
  twin: { gap: 300, minShared: 4000, maxShared: 9000, perStream: 7, minStars: 6, splits: 80, slack: 0.5, budget: 1500, search: 3000, candidates: 8, tries: [48, 16] as const, lead: [700, 1200, 400, 1800] as const },
} as const;
